// Phase 2 P2-6 — Bankroll Advisory page.
//
// User-facing bankroll dashboard. Shows total bankroll, risk profile,
// today's calibrated daily stake recommendation (with pull-back when
// recent losses exceed thresholds), 30-day P&L, lifetime deposits, and
// the recent ledger.
//
// Distinct from /vault — Vault tracks "what's been protected from chain
// wins"; Bankroll Advisory tracks "what budget the user is willing to
// risk and what daily stake the system recommends from it."

import type { CSSProperties } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { AwaitingDataPanel } from "../_awaiting-data.tsx";
import {
  useBankrollState,
  type BankrollLedgerRow,
  type DailyStakeRecommendation,
  type RiskProfile,
} from "../../bankroll/useBankrollState.ts";

// ─── Styles ───────────────────────────────────────────────────────

const pageRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

const pageHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  flexWrap: "wrap",
};

const pageTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  margin: 0,
};

const pageSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: 4,
};

const card: CSSProperties = {
  background: "var(--sz-bg-surface)",
  border: "1px solid var(--sz-border-default)",
  borderRadius: "var(--sz-radius-md, 8px)",
  padding: "var(--sz-space-3)",
};

const heroRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.4fr 1fr 1fr",
  gap: "var(--sz-space-3)",
};

const heroBig: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(2rem, 6vw, 3.6rem)",
  color: "var(--sz-text-primary)",
  letterSpacing: "1px",
  lineHeight: 1,
};

const eyebrow: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

const sectionEyebrow: CSSProperties = {
  ...eyebrow,
  margin: "var(--sz-space-3) 0 var(--sz-space-2)",
};

const recommendCard: CSSProperties = {
  ...card,
  borderLeft: "3px solid var(--sz-amber)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
};

const profileBadge = (risk: RiskProfile): CSSProperties => ({
  fontFamily: "var(--sz-font-mono)",
  fontSize: "10px",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  padding: "3px 10px",
  borderRadius: 3,
  border: "1px solid",
  display: "inline-block",
  ...(risk === "aggressive"
    ? { color: "var(--sz-warning-text)", borderColor: "var(--sz-warning-dim)" }
    : risk === "moderate"
      ? { color: "var(--sz-amber)", borderColor: "var(--sz-amber-dim)" }
      : risk === "conservative"
        ? { color: "var(--sz-success-text)", borderColor: "var(--sz-success-dim)" }
        : { color: "var(--sz-text-secondary)", borderColor: "var(--sz-border-default)" }),
});

const ledgerRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "120px 1fr 100px 120px",
  gap: "var(--sz-space-2)",
  padding: "8px 0",
  borderBottom: "1px solid var(--sz-border-subtle)",
  fontFamily: "var(--sz-font-mono)",
  fontSize: "0.8rem",
  alignItems: "baseline",
};

// ─── Helpers ──────────────────────────────────────────────────────

function fmtCurrency(amount: number | null | undefined, currency = "GHS"): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return "—";
  const sym = currency === "GHS" ? "GH₵" : currency === "NGN" ? "₦" : "$";
  return `${sym}${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(2)}%`;
}

function fmtRelativeDate(iso: string): string {
  const date = new Date(iso);
  const ms = Date.now() - date.getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

const ENTRY_KIND_LABEL: Record<BankrollLedgerRow["entry_kind"], string> = {
  deposit: "Deposit",
  withdrawal: "Withdraw",
  win: "Win",
  loss: "Loss",
  adjustment: "Adjustment",
  transfer_to_investment: "→ Investment",
};

const ENTRY_KIND_COLOR: Record<BankrollLedgerRow["entry_kind"], string> = {
  deposit: "var(--sz-success-text)",
  win: "var(--sz-success-text)",
  adjustment: "var(--sz-info-text)",
  withdrawal: "var(--sz-warning-text)",
  loss: "var(--sz-danger-text)",
  transfer_to_investment: "var(--sz-amber)",
};

// ─── Recommendation card ──────────────────────────────────────────

function RecommendationCard({
  rec,
  currency,
}: {
  rec: DailyStakeRecommendation;
  currency: string;
}): JSX.Element {
  const pulledBack = rec.pull_back_factor < 1.0;
  return (
    <article style={recommendCard}>
      <div style={eyebrow}>Today's recommended daily stake</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "var(--sz-space-3)" }}>
        <div>
          <span style={{ ...heroBig, color: "var(--sz-amber)" }}>
            {fmtCurrency(rec.recommended_daily_stake, currency)}
          </span>
          <div style={{ ...eyebrow, marginTop: 6 }}>
            Weekly: {fmtCurrency(rec.recommended_weekly_stake, currency)}
          </div>
        </div>
        <div style={{ textAlign: "right", fontFamily: "var(--sz-font-mono)", fontSize: "0.85rem" }}>
          <div style={{ color: "var(--sz-text-secondary)" }}>
            Base: {fmtPct(rec.base_pct)} of bankroll
          </div>
          {pulledBack ? (
            <div style={{ color: "var(--sz-warning-text)", marginTop: 4 }}>
              Pull-back: {(rec.pull_back_factor * 100).toFixed(0)}% (recent losses)
            </div>
          ) : (
            <div style={{ color: "var(--sz-success-text)", marginTop: 4 }}>
              Standard recommendation
            </div>
          )}
          <div style={{ color: "var(--sz-text-muted)", marginTop: 4 }}>
            7d P&L: {fmtCurrency(rec.recent_7d_pl, currency)}
          </div>
        </div>
      </div>
      <div style={{ fontSize: "0.75rem", color: "var(--sz-text-muted)" }}>
        {rec.rationale.replace(/_/g, " ")}
      </div>
    </article>
  );
}

// ─── Main page ────────────────────────────────────────────────────

export function BankrollPage(): JSX.Element {
  const { profile, state, ledger, recommendation, loading, error } = useBankrollState();

  if (loading) {
    return (
      <div style={pageRoot}>
        <div style={pageHeader}>
          <div>
            <h1 style={pageTitle}>Bankroll</h1>
            <div style={pageSubtitle}>Daily stake advisory · risk profile · ledger</div>
          </div>
        </div>
        <AwaitingDataPanel
          eyebrow="Bankroll"
          title="Loading…"
          body="Fetching your bankroll profile and stake recommendation."
        />
      </div>
    );
  }

  if (error !== null) {
    return (
      <div style={pageRoot}>
        <div style={pageHeader}>
          <div>
            <h1 style={pageTitle}>Bankroll</h1>
          </div>
        </div>
        <AwaitingDataPanel eyebrow="Bankroll" title="Read failed." body={error} />
      </div>
    );
  }

  // Empty state — user has not declared a bankroll yet
  if (!profile) {
    return (
      <div style={pageRoot}>
        <div style={pageHeader}>
          <div>
            <h1 style={pageTitle}>Bankroll</h1>
            <div style={pageSubtitle}>Daily stake advisory · risk profile · ledger</div>
          </div>
        </div>
        <AwaitingDataPanel
          eyebrow="Bankroll"
          title="Declare your bankroll to begin."
          body={
            <>
              The Bankroll Advisory engine recommends your daily stake as a percentage of your
              declared bankroll, calibrated to your risk profile and pulled back automatically
              when recent losses exceed safe thresholds.
              <br />
              <br />
              STRIKZA never debits from any account. Your declared bankroll is a budget you set
              for yourself — STRIKZA only advises on what to stake from it. Use the Sika
              conversational layer to declare your bankroll, or wait for the Phase 2 declaration
              UI to land.
            </>
          }
        />
      </div>
    );
  }

  const currency = profile.currency || "GHS";

  return (
    <div style={pageRoot}>
      <div style={pageHeader}>
        <div>
          <h1 style={pageTitle}>Bankroll</h1>
          <div style={pageSubtitle}>Daily stake advisory · risk profile · ledger</div>
        </div>
        <span style={profileBadge(profile.risk_profile)}>{profile.risk_profile}</span>
      </div>

      {/* Hero row */}
      <div style={heroRow}>
        {/* Total bankroll */}
        <article style={card}>
          <div style={eyebrow}>Total bankroll</div>
          <div style={{ ...heroBig, marginTop: 8 }}>
            {fmtCurrency(profile.total_bankroll, currency)}
          </div>
          <div style={{ ...eyebrow, marginTop: 8 }}>
            Risk profile · {profile.risk_profile}
            {profile.is_active ? "" : " · paused"}
          </div>
        </article>

        {/* 30-day P&L */}
        <article style={card}>
          <div style={eyebrow}>30-day P&L</div>
          <div
            style={{
              ...heroBig,
              fontSize: "clamp(1.4rem, 4vw, 2.4rem)",
              marginTop: 8,
              color:
                state && state.pl_30d > 0
                  ? "var(--sz-success-text)"
                  : state && state.pl_30d < 0
                    ? "var(--sz-danger-text)"
                    : "var(--sz-text-primary)",
            }}
          >
            {state ? fmtCurrency(state.pl_30d, currency) : "—"}
          </div>
          <div style={{ ...eyebrow, marginTop: 8 }}>
            {state && state.pl_30d > 0
              ? "in profit"
              : state && state.pl_30d < 0
                ? "in drawdown"
                : "flat"}
          </div>
        </article>

        {/* Lifetime deposits */}
        <article style={card}>
          <div style={eyebrow}>Lifetime deposits</div>
          <div style={{ ...heroBig, fontSize: "clamp(1.4rem, 4vw, 2.4rem)", marginTop: 8 }}>
            {state ? fmtCurrency(state.lifetime_deposits, currency) : "—"}
          </div>
          <div style={{ ...eyebrow, marginTop: 8 }}>
            Withdrawn: {state ? fmtCurrency(state.lifetime_withdrawals, currency) : "—"}
          </div>
        </article>
      </div>

      {/* Daily stake recommendation */}
      {recommendation ? (
        <RecommendationCard rec={recommendation} currency={currency} />
      ) : (
        <AwaitingDataPanel
          eyebrow="Daily stake"
          title="Recommendation unavailable."
          body="The suggest_daily_stake RPC returned no recommendation. Check that bankroll is active and not self-paused."
        />
      )}

      {/* Guardrails */}
      {(profile.max_daily_loss !== null ||
        profile.max_single_stake_pct !== null ||
        profile.paused_until !== null) && (
        <article style={card}>
          <div style={eyebrow}>Hard guardrails</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "var(--sz-space-2)",
              marginTop: 8,
              fontFamily: "var(--sz-font-mono)",
              fontSize: "0.85rem",
            }}
          >
            {profile.max_daily_loss !== null && (
              <div>
                <span style={eyebrow}>Max daily loss</span>
                <div style={{ color: "var(--sz-text-primary)", fontSize: "1rem", marginTop: 2 }}>
                  {fmtCurrency(profile.max_daily_loss, currency)}
                </div>
              </div>
            )}
            {profile.max_single_stake_pct !== null && (
              <div>
                <span style={eyebrow}>Max single stake</span>
                <div style={{ color: "var(--sz-text-primary)", fontSize: "1rem", marginTop: 2 }}>
                  {fmtPct(profile.max_single_stake_pct)}
                </div>
              </div>
            )}
            {profile.paused_until && (
              <div>
                <span style={eyebrow}>Self-paused until</span>
                <div style={{ color: "var(--sz-warning-text)", fontSize: "1rem", marginTop: 2 }}>
                  {profile.paused_until}
                </div>
              </div>
            )}
          </div>
        </article>
      )}

      {/* Recent ledger */}
      <div style={sectionEyebrow}>Recent ledger ({ledger.length})</div>
      {ledger.length === 0 ? (
        <article style={card}>
          <div style={{ fontFamily: "var(--sz-font-mono)", fontSize: "0.85rem", color: "var(--sz-text-muted)" }}>
            No bankroll events yet. Deposits, settlements, and adjustments appear here.
          </div>
        </article>
      ) : (
        <article style={card}>
          <div
            style={{
              ...ledgerRow,
              borderBottom: "1px solid var(--sz-border-default)",
              color: "var(--sz-text-muted)",
              fontSize: "0.7rem",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
            }}
          >
            <span>When</span>
            <span>Kind · ref</span>
            <span style={{ textAlign: "right" }}>Amount</span>
            <span style={{ textAlign: "right" }}>Balance</span>
          </div>
          {ledger.map((row) => (
            <div key={row.id} style={ledgerRow}>
              <span style={{ color: "var(--sz-text-secondary)" }}>{fmtRelativeDate(row.created_at)}</span>
              <span>
                <span style={{ color: ENTRY_KIND_COLOR[row.entry_kind] }}>
                  {ENTRY_KIND_LABEL[row.entry_kind]}
                </span>
                {row.reference_type ? (
                  <span style={{ color: "var(--sz-text-muted)" }}> · {row.reference_type}</span>
                ) : null}
                {row.notes ? (
                  <span style={{ color: "var(--sz-text-muted)", display: "block", fontSize: "0.7rem" }}>
                    {row.notes}
                  </span>
                ) : null}
              </span>
              <span
                style={{
                  textAlign: "right",
                  color:
                    row.entry_kind === "deposit" ||
                    row.entry_kind === "win" ||
                    row.entry_kind === "adjustment"
                      ? "var(--sz-success-text)"
                      : "var(--sz-danger-text)",
                }}
              >
                {row.entry_kind === "deposit" ||
                row.entry_kind === "win" ||
                row.entry_kind === "adjustment"
                  ? "+"
                  : "−"}
                {fmtCurrency(row.amount, currency)}
              </span>
              <span style={{ textAlign: "right", color: "var(--sz-text-primary)" }}>
                {fmtCurrency(row.balance_after, currency)}
              </span>
            </div>
          ))}
        </article>
      )}

      <div
        style={{
          fontFamily: "var(--sz-font-mono)",
          fontSize: "0.7rem",
          color: "var(--sz-text-muted)",
          letterSpacing: "1px",
          textTransform: "uppercase",
          textAlign: "center",
          marginTop: "var(--sz-space-2)",
        }}
      >
        <Icon name="info" size={12} /> STRIKZA never debits from your bankroll. All recommendations
        are advisory. Every confirmation is yours to make.
      </div>
    </div>
  );
}
